import {RequestMethod} from '#lit/common/constants/index.js';
import type {Dataset} from '#lit/types/datasets.js';
import {FlyErrorWarning} from '#lit/utils/errors.js';
import {fetcher} from '#lit/utils/http.js';
import type {QuerviceTableSchema} from '#lit/types/quervice.js';

class QuerviceService {
  async fetchFullSchema(
    dataset_group_id: number,
    datasets: Dataset[],
    audience_id?: number,
    full = true
  ): Promise<Map<number, QuerviceTableSchema> | void> {
    try {
      const res = await this.fetchSchema(dataset_group_id, audience_id, full);

      if (!res || !res.data) {
        throw Error('Invalid schema request via Quervice.');
      }

      const tableSchemaMap = new Map<number, QuerviceTableSchema>();
      datasets.forEach(dataset => {
        // table key defined in Flywheel::Warehouse::Base.table_identifier
        const tableKey = `${dataset.database}->${dataset.dataset_without_db}->${dataset.source_table}`;
        const table = res.data[tableKey];

        if (table) {
          tableSchemaMap.set(dataset.id, table);
        }
      });

      return tableSchemaMap;
    } catch (err) {
      const errorDesc = `dataset group: ${dataset_group_id}`;
      new FlyErrorWarning(
        `Error fetching schema for ${errorDesc}`,
        {
          label: 'request',
          data: 'GET: /api/v2/dataset_groups/:id/builder_schema',
        },
        {
          label: 'response',
          data: err,
        }
      ).register('Error AudienceBuilder');
    }
  }

  async fetchSchema(
    id: number,
    audience_id?: number,
    full = true // If true, the backend can fallback to Quervice if the local schema is blank
  ): Promise<{
    data: Record<string, QuerviceTableSchema>;
  }> {
    let params = full ? '?full=true' : '?full=false';
    if (audience_id !== undefined) {
      params += `&audience_id=${audience_id}`;
    }
    return await fetcher<{
      data: Record<string, QuerviceTableSchema>;
    }>(`/api/v2/dataset_groups/${id}/builder_schema${params}`, {
      method: RequestMethod.GET,
    });
  }
}

export const querviceService = new QuerviceService();
